import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'

import svgHelpers from '../../../helpers/functions/svgHelpers'
import Style from './style'
import { FALLBACK_SPACE_TYPE_THEME, SPACE_TYPE } from '../../../helpers/constants/listRoom'
import { hexToRgba } from '../../../helpers'
import { FloorPlanLabel } from '../../../components/FloorPlanLabel'
import { ReactComponent as DeskIcon } from '../../../themes/images/ic_desk.svg'
import { ReactComponent as RoomIcon } from '../../../themes/images/ic_room.svg'
import { ReactComponent as OtherIcon } from '../../../themes/images/ic_other.svg'
import { ReactComponent as UserIcon } from '../../../themes/images/ic_lucide_user.svg'

const SPACE_TYPE_ICON = {
  [SPACE_TYPE.DESK]: DeskIcon,
  [SPACE_TYPE.ROOM]: RoomIcon,
  [SPACE_TYPE.OTHER]: OtherIcon,
}

const BADGE_PX = 28
const ICON_PX = 18
const LABEL_MIN_PX = 40
const LABEL_SPACING_PX = 8
const LABEL_MAX_PX = 160

const showsOwner = (room) => (
  room.spaceType === SPACE_TYPE.DESK && Number(room.isUsing) === 1 && Boolean(room.ownerName)
)

const getSpaceLabel = (room) => (showsOwner(room) ? room.ownerName : room.roomName)

class LinkOverlayComponent extends React.Component {
  constructor(props) {
    super(props);

    this.areaRef = React.createRef();
    this.textRef = React.createRef();
    this.circleRef = React.createRef();
    this.svgRef = React.createRef();
    this.state = { renderedWidth: 0, hoveredId: null };
  }

  setHoveredId (hoveredId) {
    if (this.state.hoveredId !== hoveredId) this.setState({ hoveredId })
  }

  componentDidMount () {
    const svg = this.svgRef.current
    if (!svg) return
    const measure = () => this.setState({ renderedWidth: svg.getBoundingClientRect().width })
    measure()
    this.resizeObserver = new ResizeObserver(measure)
    this.resizeObserver.observe(svg)
  }

  componentWillUnmount () {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  getScale () {
    const { imageWidth } = this.props
    return this.state.renderedWidth > 0 && imageWidth > 0 ? this.state.renderedWidth / imageWidth : 1
  }

  renderLink (id, coord, isUsing, name) {
    const className = [
      isUsing ? 'busy' : 'available',
      'pointer',
      this.state.hoveredId === id ? 'is-hovered' : '',
    ].filter(Boolean).join(' ')
    const colorTheme = this.props.settingTheme?.colorTheme
    const busyVars = isUsing
      ? {
          '--busy-fill': hexToRgba(colorTheme, 0.7),
          '--busy-fill-hover': hexToRgba(colorTheme, 0.9),
        }
      : undefined
    return (
      <React.Fragment key={id} >
        <g
          className={className}
          key={id}
          style={busyVars}
          onClick={(event) => this.props.handleClick(event, id)}
          onMouseEnter={() => this.setHoveredId(id)}
          onMouseLeave={() => this.setHoveredId(null)}
        >
          <polygon points={coord} vectorEffect='non-scaling-stroke' ref={this.areaRef}/>
        </g>
      </React.Fragment>
    )
  }

  renderIcon (id, nickname, centerPoint, shape, isUsing, spaceType = 'other', isOwnerShown = false) {
    try {
      centerPoint = JSON.parse(centerPoint)
      shape = JSON.parse(shape)
    } catch (_) {
      return null
    }
    if (centerPoint == null || shape == null) return null

    const theme = this.props.themeConfig?.spaceType?.[spaceType] || FALLBACK_SPACE_TYPE_THEME[spaceType] || FALLBACK_SPACE_TYPE_THEME.other;
    const bg = theme.background || theme.bg;
    const fg = theme.foreground || theme.text;
    const Icon = isOwnerShown ? UserIcon : SPACE_TYPE_ICON[spaceType] || OtherIcon;
    
    const colorTheme = this.props.settingTheme?.colorTheme
    const scale = this.getScale();
    const badgeSize = BADGE_PX / scale;
    const iconSize = ICON_PX / scale;
    const labelMaxWidth = Math.min(Math.max(shape.width * scale - LABEL_SPACING_PX, LABEL_MIN_PX), LABEL_MAX_PX);
    const left = centerPoint.x - shape.width / 2;
    const top = centerPoint.y - shape.height / 2;
    const busyPillStyle = isUsing
      ? { boxShadow: `0 2px 6px ${hexToRgba(colorTheme, 0.28)}` }
      : undefined;

    return (
      <g
        key={'icon_' + id}
        className="room-group"
        onMouseEnter={() => this.setHoveredId(id)}
        onMouseLeave={() => this.setHoveredId(null)}
      >
        <foreignObject
          x={left}
          y={top}
          width={shape.width}
          height={shape.height}
          className='pointer room-foreign-object'
          onClick={(event) => this.props.handleClick(event, id)}
          style={{ overflow: 'visible' }}
          ref={this.circleRef}
        >
          <div className='room-box-inner' style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
          }}
          />
        </foreignObject>
        <g className='pointer room-badge' onClick={(event) => this.props.handleClick(event, id)}>
          <circle cx={centerPoint.x} cy={centerPoint.y} r={badgeSize / 2} fill={bg} />
          <Icon
            x={centerPoint.x - iconSize / 2}
            y={centerPoint.y - iconSize / 2}
            width={iconSize}
            height={iconSize}
            style={{ color: fg }}
          />
        </g>
        <foreignObject
          x={centerPoint.x}
          y={centerPoint.y + badgeSize / 2 + 4 / scale}
          width={1}
          height={1}
          className='room-label'
          style={{ pointerEvents: 'none', overflow: 'visible' }}
        >
          <FloorPlanLabel name={nickname} maxWidth={labelMaxWidth} scale={scale} style={busyPillStyle} />
        </foreignObject>
      </g>
    )
  }

  render () {
    const props = Object.assign({}, this.props)
    const width = '100%'
    const height = '100%'
    return (
      <Style
        colorTheme={this.props.settingTheme.colorTheme}
      >
        <svg
          ref={this.svgRef}
          id='linkOverlay'
          className='linkOverlay'
          viewBox={svgHelpers.getViewBox(props.imageWidth, props.imageHeight)}
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='none'
          style={{ width: (props.width != null) ? props.width : width, height: (props.height != null) ? props.height : height }}
        >
          {props.roomList.map((room) => this.renderLink(room.roomId, room.coord, room.isUsing, room.roomName))}
          {props.roomList.map((room) => this.renderIcon(room.roomId, getSpaceLabel(room), room.centerPoint, room.shape, room.isUsing, room.spaceType, showsOwner(room)))}
        </svg>
      </Style>
    )
  }
}

LinkOverlayComponent.propTypes = {
  roomList: PropTypes.array.isRequired,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number
}

export default LinkOverlayComponent

